import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import AreasService from "../../services/AreasService";
import DoctoresService from "../../services/DoctoresService";
import EstudiosService from "../../services/EstudiosService";
import SolicitudesEstudiosService from "../../services/SolicitudesEstudiosService";
import TelefonosService from "../../services/TelefonosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const OrdenDia = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [areas, setAreas] = useState([]);
  const [doctoresMap, setDoctoresMap] = useState({});
  const [catalogEstudiosMap, setCatalogEstudiosMap] = useState({});
  const [solicitudesEstudiosMap, setSolicitudesEstudiosMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [areaSeleccionada, setAreaSeleccionada] = useState("todas");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  useEffect(() => {
    document.title = "Orden del Día";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resAreas, resDoc, resEst] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        AreasService.getAreas().catch(() => ({ data: [] })),
        DoctoresService.getDoctores().catch(() => ({ data: [] })),
        EstudiosService.getEstudios().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaAreas = Array.isArray(resAreas?.data?.areas)
        ? resAreas.data.areas
        : Array.isArray(resAreas?.data)
        ? resAreas.data
        : [];

      const listaDoc = Array.isArray(resDoc?.data?.doctores)
        ? resDoc.data.doctores
        : Array.isArray(resDoc?.data)
        ? resDoc.data
        : [];

      const listaEst = Array.isArray(resEst?.data?.estudios)
        ? resEst.data.estudios
        : Array.isArray(resEst?.data)
        ? resEst.data
        : [];

      // Mapear doctores por idDoctor o id
      const docMap = {};
      listaDoc.forEach((d) => {
        if (d && (d.idDoctor || d.id)) {
          const idKey = d.idDoctor || d.id;
          const nom = [d.nombre, d.apPaterno, d.apMaterno].filter(Boolean).join(" ").trim();
          docMap[idKey] = nom || d.nombreDoctor || `Dr. #${idKey}`;
        }
      });

      // Mapear catálogo de estudios con idArea
      const catEstMap = {};
      listaEst.forEach((e) => {
        if (e && (e.idEstudio || e.id)) {
          const idKey = e.idEstudio || e.id;
          catEstMap[idKey] = {
            idEstudio: idKey,
            codigo: e.codigo || e.clave || "",
            nombre: e.nombre || e.nombreEstudio || "",
            idArea: e.idArea,
          };
        }
      });

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
      setAreas(listaAreas);
      setDoctoresMap(docMap);
      setCatalogEstudiosMap(catEstMap);

      // Cargar estudios para cada solicitud vía SolicitudesEstudiosService
      const estPorSolMap = {};
      await Promise.all(
        listaSol.map(async (sol) => {
          if (sol && (sol.idSolicitud || sol.id)) {
            const solId = sol.idSolicitud || sol.id;
            try {
              const resEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(solId);
              const listEst = Array.isArray(resEst)
                ? resEst
                : Array.isArray(resEst?.estudios)
                ? resEst.estudios
                : Array.isArray(resEst?.data)
                ? resEst.data
                : [];
              if (listEst.length > 0) {
                estPorSolMap[solId] = listEst;
              }
            } catch (e) {}
          }
        })
      );

      setSolicitudesEstudiosMap(estPorSolMap);
    } catch (error) {
      console.error("Error al cargar datos de Orden del Día:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al consultar la información del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  // Formatear edad del paciente
  const formatEdadPaciente = (sol) => {
    let edadVal = sol.edad || sol.pacienteEdad || sol.edadPaciente || sol.pacienteObj?.edad || "";
    if (!edadVal && sol.fechaNacimiento) {
      try {
        const nac = new Date(sol.fechaNacimiento);
        const hoy = new Date();
        let diff = hoy.getFullYear() - nac.getFullYear();
        if (
          hoy.getMonth() < nac.getMonth() ||
          (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())
        ) {
          diff--;
        }
        if (diff >= 0) edadVal = `${diff} Años`;
      } catch (e) {}
    }

    if (!edadVal) return "";
    const str = String(edadVal).trim();
    if (/a[nñ]o|mes|d[ií]a/i.test(str)) {
      return str;
    }
    if (!isNaN(Number(str))) {
      return `${str} Años`;
    }
    return str;
  };

  // Formatear nombre completo del paciente
  const getNombrePaciente = (sol) => {
    const nom = [sol.nombrePaciente, sol.apPaternoPaciente, sol.apMaternoPaciente]
      .filter(Boolean)
      .join(" ")
      .trim();
    return nom || sol.paciente || sol.nombreCompletoPaciente || sol.pacienteNombre || "PACIENTE";
  };

  // Formatear doctor(es) de la solicitud exhaustivamente
  const getNombreDoctor = (sol) => {
    const nombresArr = [];

    // 1. Arreglo idDoctores o idDoctoresN
    const idsArr = Array.isArray(sol.idDoctores)
      ? sol.idDoctores
      : Array.isArray(sol.idDoctoresN)
      ? sol.idDoctoresN
      : [];

    idsArr.forEach((id) => {
      if (id && doctoresMap[id]) {
        nombresArr.push(doctoresMap[id]);
      }
    });

    // 2. Arreglo de objetos doctores
    if (nombresArr.length === 0 && Array.isArray(sol.doctores)) {
      sol.doctores.forEach((d) => {
        if (typeof d === "string") nombresArr.push(d);
        else if (d) {
          const nom = [d.nombre, d.apPaterno, d.apMaterno].filter(Boolean).join(" ").trim() || d.nombreDoctor || d.doctor;
          if (nom) nombresArr.push(nom);
        }
      });
    }

    // 3. Campos directos idDoctor, idDoctor1, idDoctor2, idDoctor3, etc.
    if (nombresArr.length === 0) {
      const directIds = [sol.idDoctor, sol.idDoctor1, sol.idDoctor2, sol.idDoctor3, sol.id_doctor, sol.doctorId, sol.idDoctorRemitido];
      directIds.forEach((id) => {
        if (id && doctoresMap[id] && !nombresArr.includes(doctoresMap[id])) {
          nombresArr.push(doctoresMap[id]);
        }
      });
    }

    // 4. Campos directos de texto nombreDoctor, doctor, etc.
    if (nombresArr.length === 0) {
      const directNames = [sol.nombreDoctor, sol.doctor, sol.nombreDoctor1, sol.nombreDoctor2, sol.doctorNombre].filter(Boolean);
      directNames.forEach((n) => {
        if (n && typeof n === "string" && !nombresArr.includes(n.trim())) {
          nombresArr.push(n.trim());
        }
      });
    }

    if (nombresArr.length === 0) {
      return "A QUIEN CORRESPONDA";
    }

    return nombresArr
      .map((nom) => (nom.toLowerCase().startsWith("dr") ? nom : `Dr(a). ${nom}`))
      .join(", ");
  };

  // Formatear estatus de resultados
  const formatEstadoResultados = (sol) => {
    const raw = sol.estatusResultados || sol.estatusResultado || sol.estatusEstudios || sol.estatus || "";
    if (!raw) return "PENDIENTE";
    const est = String(raw).toUpperCase().trim();
    if (est === "E" || est === "EN PROCESO" || est === "PENDIENTE") return "EN PROCESO";
    if (est === "R" || est === "REPORTADO") return "REPORTADO";
    if (est === "V" || est === "REVISADO") return "REVISADO";
    if (est === "L" || est === "LIBERADO") return "LIBERADO";
    if (est === "EN" || est === "ENTREGADO") return "ENTREGADO";
    if (est === "RP" || est === "RESULTADOS PARCIALES") return "RESULTADOS PARCIALES";
    return est;
  };

  // Obtener estilo de color del estatus
  const getEstadoStyle = (estatusStr) => {
    if (["LIBERADO", "ENTREGADO"].includes(estatusStr)) return { color: "#2e7d32", fontWeight: "bold" };
    if (["REPORTADO", "RESULTADOS PARCIALES"].includes(estatusStr)) return { color: "#ed6c02", fontWeight: "bold" };
    if (["REVISADO"].includes(estatusStr)) return { color: "#0288d1", fontWeight: "bold" };
    return { color: "#18234f", fontWeight: "bold" };
  };

  // Extraer estudios filtrados por Área de una solicitud
  const getEstudiosDeSolicitud = (sol) => {
    const solId = sol.idSolicitud || sol.id;
    let rawEstudios = solicitudesEstudiosMap[solId] || sol.estudios || sol.solicitudEstudios || sol.detalles || [];
    let list = [];

    if (Array.isArray(rawEstudios) && rawEstudios.length > 0) {
      list = rawEstudios.map((e) => {
        const idEst = e.idEstudio || e.id;
        const catObj = catalogEstudiosMap[idEst] || {};
        return {
          idEstudio: idEst,
          nombre: e.nombreEstudio || e.nombre || e.estudio || catObj.nombre || `Estudio #${idEst}`,
          codigo: e.codigo || catObj.codigo || "",
          idArea: e.idArea || catObj.idArea,
        };
      });
    }

    // Si se seleccionó una área específica, filtrar los estudios de esa área
    if (areaSeleccionada !== "todas") {
      list = list.filter((e) => String(e.idArea) === String(areaSeleccionada));
    }

    return list;
  };

  // Filtrado de solicitudes por Sucursal, Área y Rango de Fechas
  const solicitudesFiltradas = useMemo(() => {
    return solicitudes.filter((sol) => {
      // 1. Filtro Sucursal
      if (sucursalSeleccionada !== "todas") {
        if (String(sol.idSucursal) !== String(sucursalSeleccionada)) {
          return false;
        }
      }

      // 2. Filtro Rango de Fechas (formato YYYY-MM-DD)
      const rawFecha = sol.fecha || sol.createdAt || sol.fechaCreacion || sol.fechaSolicitud || "";
      const fechaSolStr = String(rawFecha).substring(0, 10);
      if (fechaDe && fechaSolStr < fechaDe) return false;
      if (fechaA && fechaSolStr > fechaA) return false;

      // 3. Filtro Área (la solicitud debe contener al menos un estudio del área seleccionada)
      if (areaSeleccionada !== "todas") {
        const estList = getEstudiosDeSolicitud(sol);
        if (estList.length === 0) return false;
      }

      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursalSeleccionada, areaSeleccionada, fechaDe, fechaA, catalogEstudiosMap, solicitudesEstudiosMap]);

  // Agrupación por Sucursal para renderizado de tablas
  const solicitudesPorSucursal = useMemo(() => {
    const map = {};

    solicitudesFiltradas.forEach((sol) => {
      const sucId = sol.idSucursal || 0;
      let sucNom = sol.nombreSucursal || sol.sucursal || "";
      if (!sucNom && sucId) {
        const sObj = sucursales.find((s) => String(s.idSucursal) === String(sucId));
        if (sObj) sucNom = sObj.nombre || sObj.nombreSucursal;
      }
      if (!sucNom) sucNom = "SUCURSAL GENERAL";

      if (!map[sucId]) {
        map[sucId] = {
          idSucursal: sucId,
          nombreSucursal: sucNom.toUpperCase(),
          items: [],
        };
      }
      map[sucId].items.push(sol);
    });

    return Object.values(map);
  }, [solicitudesFiltradas, sucursales]);

  // Exportar Orden del Día a PDF Institucional
  const handleExportarPDF = async () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen solicitudes registradas para los filtros seleccionados.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales[0] || {};
    if (sucursalSeleccionada !== "todas") {
      const sFound = sucursales.find((s) => String(s.idSucursal) === String(sucursalSeleccionada));
      if (sFound) sucursalActualObj = sFound;
    }

    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) {}
    }

    const sucursalNomHeader = (sucursalActualObj.nombre || "LABORATORIO").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) {}

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos sucursal a la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomHeader, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título del reporte
    const areaObj = areas.find((a) => String(a.idArea) === String(areaSeleccionada));
    const areaNomStr = areaObj ? (areaObj.nombre || areaObj.nombreArea || "") : "Todas las áreas";

    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text("ORDEN DEL DÍA", 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Rango de Fechas: De ${fechaDe} A ${fechaA} | Área: ${areaNomStr}`, 14, currY + 4);

    let nextY = currY + 10;
    const bodyRowsPdf = [];

    solicitudesPorSucursal.forEach((group) => {
      // Fila de encabezado por sucursal
      bodyRowsPdf.push([
        {
          content: `SUCURSAL: ${group.nombreSucursal}`,
          colSpan: 5,
          styles: { fontStyle: "bold", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontSize: 8 },
        },
      ]);

      group.items.forEach((sol, idx) => {
        const cod = sol.codigo || sol.codigoSolicitud || String(sol.idSolicitud);
        const fechaSolStr = String(sol.fecha || sol.createdAt || sol.fechaCreacion || sol.fechaSolicitud || "").substring(0, 10);

        const pacNom = getNombrePaciente(sol);
        const edadStr = formatEdadPaciente(sol);
        const pacConEdad = edadStr ? `${pacNom} (${edadStr})` : pacNom;
        const docNom = getNombreDoctor(sol);

        const estadoRes = formatEstadoResultados(sol);

        const estList = getEstudiosDeSolicitud(sol);
        const detalleEstStr = estList.length > 0 ? estList.map((e) => e.nombre).join("\n• ") : "Sin estudios registrados";
        const detalleConBullet = estList.length > 0 ? `• ${detalleEstStr}` : detalleEstStr;

        bodyRowsPdf.push([
          String(idx + 1),
          `${cod}\n${fechaSolStr}`,
          `${pacConEdad}\n${docNom}`,
          estadoRes,
          detalleConBullet,
        ]);
      });
    });

    autoTable(doc, {
      startY: nextY,
      head: [["No.", "Código / Fecha", "Paciente / Doctor", "Estado", "Detalle de Estudios"]],
      body: bodyRowsPdf,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 2, valign: "top" },
      columnStyles: {
        0: { width: 10, halign: "center" },
        1: { width: 30 },
        2: { width: 55 },
        3: { width: 30, halign: "center" },
        4: { width: 65 },
      },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Orden_del_Dia_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte Orden del Día en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  ORDEN DEL DÍA
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros de Búsqueda
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((s) => (
                            <MenuItem key={s.idSucursal} value={String(s.idSucursal)}>
                              {s.nombre || s.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Áreas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Áreas:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={areaSeleccionada}
                          onChange={(e) => setAreaSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas las áreas</MenuItem>
                          {areas.map((a) => (
                            <MenuItem key={a.idArea} value={String(a.idArea)}>
                              {a.nombre || a.nombreArea}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Seleccione un rango de fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item>
                            <Typography variant="body2" component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                              De:
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={fechaDe}
                              onChange={(e) => setFechaDe(e.target.value)}
                              sx={{ width: "170px" }}
                            />
                          </Grid>
                          <Grid item>
                            <Typography variant="body2" component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                              A:
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={fechaA}
                              onChange={(e) => setFechaA(e.target.value)}
                              sx={{ width: "170px" }}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Contenedor Principal de la Tabla por Sucursal (Sin Paginación) */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : solicitudesFiltradas.length === 0 ? (
                <Box
                  sx={{
                    width: "95%",
                    maxWidth: "1200px",
                    textAlign: "center",
                    py: 4,
                    mb: 3,
                    backgroundColor: "#fff8e1",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: "#b78103", fontWeight: "bold" }}>
                    No existen solicitudes para los filtros seleccionados.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                  {/* Resumen Total */}
                  <Box sx={{ mb: 2, textAlign: "right" }}>
                    <Typography variant="body2" sx={{ color: "#006787", fontWeight: "bold" }}>
                      Total de solicitudes encontradas: {solicitudesFiltradas.length}
                    </Typography>
                  </Box>

                  {/* Renderizado Continuo de Tablas Agrupadas por Sucursal */}
                  {solicitudesPorSucursal.map((sucGroup) => (
                    <Box key={sucGroup.idSucursal} sx={{ mb: 4 }}>
                      <Table
                        sx={{
                          width: "100%",
                          backgroundColor: "white",
                          border: "1px solid #c4d1db",
                          borderRadius: "8px",
                          overflow: "hidden",
                          "& .MuiTableCell-root": { padding: "10px 12px", fontSize: "0.85rem" },
                        }}
                      >
                        {/* Cabecera de la Sucursal */}
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#18234f" }}>
                            <TableCell
                              colSpan={5}
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                backgroundColor: "#18234f",
                                fontSize: "0.95rem",
                                py: 1.2,
                              }}
                            >
                              SUCURSAL: {sucGroup.nombreSucursal}
                            </TableCell>
                          </TableRow>

                          {/* Columnas principales */}
                          <TableRow sx={{ backgroundColor: "#006787" }}>
                            <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "5%", textAlign: "center" }}>
                              No.
                            </TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "18%" }}>
                              Código
                            </TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "32%" }}>
                              Paciente
                            </TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "15%", textAlign: "center" }}>
                              Estado
                            </TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "30%" }}>
                              Detalle
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {sucGroup.items.map((sol, idx) => {
                            const cod = sol.codigo || sol.codigoSolicitud || String(sol.idSolicitud);
                            const rawFecha = sol.fecha || sol.createdAt || sol.fechaCreacion || sol.fechaSolicitud || "";
                            const fechaSolStr = String(rawFecha).substring(0, 10);

                            const pacNom = getNombrePaciente(sol);
                            const edadStr = formatEdadPaciente(sol);
                            const pacConEdad = edadStr ? `${pacNom} (${edadStr})` : pacNom;
                            const docNom = getNombreDoctor(sol);

                            const estadoRes = formatEstadoResultados(sol);
                            const estList = getEstudiosDeSolicitud(sol);

                            return (
                              <TableRow key={sol.idSolicitud || idx} sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                                {/* Columna 1: No. */}
                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                  {idx + 1}
                                </TableCell>

                                {/* Columna 2: Código (arriba código, abajo fecha de creación) */}
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#18234f" }}>
                                    {cod}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 0.3 }}>
                                    {fechaSolStr}
                                  </Typography>
                                </TableCell>

                                {/* Columna 3: Paciente (arriba nombre + edad, abajo doctores) */}
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#000" }}>
                                    {pacConEdad}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#555", fontStyle: "italic", display: "block", mt: 0.3 }}>
                                    {docNom}
                                  </Typography>
                                </TableCell>

                                {/* Columna 4: Estado (estatus de resultados) */}
                                <TableCell align="center">
                                  <Typography variant="body2" sx={getEstadoStyle(estadoRes)}>
                                    {estadoRes}
                                  </Typography>
                                </TableCell>

                                {/* Columna 5: Detalle (nombres de los estudios) */}
                                <TableCell>
                                  {estList.length === 0 ? (
                                    <Typography variant="caption" sx={{ color: "#888", fontStyle: "italic" }}>
                                      Sin estudios en este filtro
                                    </Typography>
                                  ) : (
                                    <Stack spacing={0.3}>
                                      {estList.map((e, eIdx) => (
                                        <Typography key={eIdx} variant="body2" sx={{ fontSize: "0.82rem", color: "#222" }}>
                                          • {e.nombre}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón Flotante Lateral Derecho: PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Orden del Día a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default OrdenDia;
